export type PublicAppMode = 'production' | 'development';
export type PublicSiteStage = 'ru' | 'global';

type PublicCode = string | number;

function normalizeTemplate(value?: string | null): string | null {
  const normalized = String(value ?? '').trim();
  if (!normalized || !normalized.includes('{code}')) {
    return null;
  }
  return normalized;
}

function normalizeConfiguredBase(value?: string | null): string | null {
  const normalized = normalizeBaseUrl(value);
  if (!normalized || normalized.includes('{code}')) return null;
  return normalized;
}

function normalizePublicCode(value?: PublicCode | null): string | null {
  if (value === null || value === undefined) return null;
  const normalized = String(value).trim();
  return normalized ? normalized : null;
}

function normalizeBaseUrl(value?: string | null): string | null {
  const normalized = String(value ?? '').trim().replace(/\/+$/, '');
  return normalized || null;
}

function getPublicPrefix(mode: PublicAppMode): string {
  return mode === 'production' ? 'https://m' : 'https://dev-m';
}

function getPublicDomain(stage: PublicSiteStage): string {
  return stage === 'global' ? 'beclients.pro' : 'beclients.ru';
}

export function buildPublicOrigin(params: {
  publicCode?: PublicCode | null;
  mode: PublicAppMode;
  siteStage?: PublicSiteStage | null;
  fallbackBaseUrl?: string | null;
  publicUrlTemplate?: string | null;
}): string | null {
  const publicCode = normalizePublicCode(params.publicCode);
  if (publicCode) {
    const template = normalizeTemplate(params.publicUrlTemplate);
    if (template) {
      return normalizeBaseUrl(template.replace('{code}', publicCode));
    }
    return `${getPublicPrefix(params.mode)}${publicCode}.${getPublicDomain(params.siteStage ?? 'ru')}`;
  }

  return normalizeBaseUrl(params.fallbackBaseUrl);
}

export function buildBookingUrl(params: {
  publicCode?: PublicCode | null;
  masterSlug?: string | null;
  mode: PublicAppMode;
  siteStage?: PublicSiteStage | null;
  fallbackBaseUrl?: string | null;
  publicUrlTemplate?: string | null;
}): string | null {
  const masterSlug = String(params.masterSlug ?? '').trim();
  const configuredBase = normalizeConfiguredBase(params.publicUrlTemplate);
  if (configuredBase) {
    if (masterSlug) return `${configuredBase}/booking/${encodeURIComponent(masterSlug)}`;
    const publicCode = normalizePublicCode(params.publicCode);
    return publicCode ? `${configuredBase}/?code=${encodeURIComponent(publicCode)}` : configuredBase;
  }

  const origin = buildPublicOrigin(params);
  if (!origin) return null;
  if (!masterSlug) return origin;
  return `${origin}/booking/${masterSlug}`;
}

export function buildManageUrl(params: {
  shortCode?: string | null;
  publicCode?: PublicCode | null;
  mode: PublicAppMode;
  siteStage?: PublicSiteStage | null;
  fallbackBaseUrl?: string | null;
  publicUrlTemplate?: string | null;
}): string | null {
  const shortCode = String(params.shortCode ?? '').trim();
  if (!shortCode) return null;

  const configuredBase = normalizeConfiguredBase(params.publicUrlTemplate);
  if (configuredBase) return `${configuredBase}/r/${encodeURIComponent(shortCode)}`;

  const origin = buildPublicOrigin(params);
  if (!origin) return null;
  return `${origin}/r/${encodeURIComponent(shortCode)}`;
}

export const buildShortManageUrl = buildManageUrl;
